type BusinessContextTraceDetails = Record<string, unknown>

interface BusinessContextTraceEvent {
  sequence: number
  at: string
  pageId: string
  event: string
  details: BusinessContextTraceDetails
}

interface BusinessContextTraceApi {
  clear: () => void
  dump: () => string
  snapshot: () => BusinessContextTraceEvent[]
}

type BusinessContextTraceWindow = Window & typeof globalThis & {
  __TEASHOP_CONTEXT_TRACE__?: BusinessContextTraceApi
}

const TRACE_STORAGE_KEY = "teashop-business-context-trace-v1"
const MAX_TRACE_EVENTS = 300
const SENSITIVE_KEY_PATTERN = /token|authorization|cookie|authcode|password|secret|phone|mobile/i
const pageId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
let traceSequence = 0
let installed = false

function isTraceEnabled() {
  if (typeof window === "undefined") return false
  if (import.meta.env.MODE === "test") return false
  const hostname = window.location.hostname.toLowerCase()
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
}

function sanitizeTraceValue(
  value: unknown,
  key = "",
  depth = 0,
): unknown {
  if (SENSITIVE_KEY_PATTERN.test(key)) return "[redacted]"
  if (value == null || typeof value === "boolean" || typeof value === "number") return value
  if (typeof value === "string") {
    return value.length > 500 ? `${value.slice(0, 500)}…` : value
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    }
  }
  if (depth >= 4) return "[max-depth]"
  if (Array.isArray(value)) {
    return value.slice(0, 30).map(item => sanitizeTraceValue(item, "", depth + 1))
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 50)
        .map(([entryKey, entryValue]) => [
          entryKey,
          sanitizeTraceValue(entryValue, entryKey, depth + 1),
        ]),
    )
  }
  return String(value)
}

function readTraceEvents(): BusinessContextTraceEvent[] {
  if (!isTraceEnabled()) return []
  try {
    const parsed = JSON.parse(sessionStorage.getItem(TRACE_STORAGE_KEY) || "[]")
    return Array.isArray(parsed) ? parsed.slice(-MAX_TRACE_EVENTS) : []
  } catch {
    return []
  }
}

function writeTraceEvents(events: BusinessContextTraceEvent[]) {
  try {
    sessionStorage.setItem(
      TRACE_STORAGE_KEY,
      JSON.stringify(events.slice(-MAX_TRACE_EVENTS)),
    )
  } catch {
    // Diagnostics must never affect the business flow.
  }
}

export function recordBusinessContextTrace(
  event: string,
  details: BusinessContextTraceDetails = {},
) {
  if (!isTraceEnabled()) return
  const traceEvent: BusinessContextTraceEvent = {
    sequence: ++traceSequence,
    at: new Date().toISOString(),
    pageId,
    event,
    details: sanitizeTraceValue(details) as BusinessContextTraceDetails,
  }
  const events = [...readTraceEvents(), traceEvent].slice(-MAX_TRACE_EVENTS)
  writeTraceEvents(events)
  console.info("[business-context-trace]", traceEvent)
}

export function installBusinessContextDiagnostics() {
  if (!isTraceEnabled() || installed) return
  installed = true
  const traceWindow = window as BusinessContextTraceWindow
  traceWindow.__TEASHOP_CONTEXT_TRACE__ = {
    clear: () => {
      try {
        sessionStorage.removeItem(TRACE_STORAGE_KEY)
      } catch {
        // Diagnostics must never affect the business flow.
      }
      recordBusinessContextTrace("trace.cleared")
    },
    dump: () => JSON.stringify({
      generatedAt: new Date().toISOString(),
      page: `${window.location.pathname}${window.location.hash}`,
      events: readTraceEvents(),
    }, null, 2),
    snapshot: () => readTraceEvents(),
  }

  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined
  recordBusinessContextTrace("page.loaded", {
    navigationType: navigation?.type ?? "unknown",
    path: `${window.location.pathname}${window.location.hash}`,
  })
  window.addEventListener("beforeunload", () => {
    recordBusinessContextTrace("page.beforeunload", {
      path: `${window.location.pathname}${window.location.hash}`,
    })
  })
}
